import { flash } from "@xola/ui-kit";
import { isExperiencePurchaseItem, isOtherSellerPurchase, shouldAutoExpandRosterNotes } from "common";
import { assign } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Event, Note, Permission, Purchase, PurchaseItem } from "types";
import { Accordion } from "../../Common/Accordion";
import { CustomerNoteList, EventNoteList, PurchaseNoteList } from "../../Common/NoteList";
import { CreateCustomerNoteModal } from "../../Customers/Contacts/CreateCustomerNoteModal";
import { CreateEventNoteModal } from "../../Event/CreateEventNoteModal";
import { CreatePurchaseNoteModal } from "../../PurchaseModification/CreatePurchaseNoteModal";
import { NotesBadge } from "../Common/NotesBadge";
import { BookingNoteList } from "./BookingNoteList";
import { AddNoteButton, NotesHeading } from "./NotesComponents";
import { api } from "@/api/api";
import { populateResource } from "@/hooks/helper";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getSeller } from "@/utils/getSeller";

export enum AddNoteOption {
    PURCHASE_NOTE,
    EVENT_NOTE,
    CUSTOMER_NOTE,
}

interface NotesListPerTypeProps {
    event?: Event;
    purchase: Purchase;
    userId?: string;
    className?: string;
    showCurrentPurchaseNotes?: boolean;
    item?: PurchaseItem;
}

export const NotesListPerType = ({
    event,
    purchase,
    userId,
    className,
    showCurrentPurchaseNotes,
    item,
}: NotesListPerTypeProps) => {
    const seller = getSeller();
    const invalidate = useInvalidate();

    const [selectedAddNoteOption, setSelectedAddNoteOption] = useState<AddNoteOption | null>();
    const handleModalClose = () => setSelectedAddNoteOption(null);

    const shouldAutoExpandNotes = shouldAutoExpandRosterNotes(seller);

    const sellerCustomerNotes = seller.customerNotes;
    const customerNotes =
        userId && sellerCustomerNotes && !isOtherSellerPurchase(purchase, seller.id)
            ? Object.values(sellerCustomerNotes[userId] ?? {})
            : [];
    const customerNoteCount = customerNotes.length;
    const eventNoteCount = event?.notes?.length ?? 0;
    const purchaseNoteCount = purchase?.notes?.length ?? 0;
    const showEventNotes = item && isExperiencePurchaseItem(item) && item.seller?.id === getSeller().id;
    let totalNoteCount = customerNoteCount + purchaseNoteCount;
    if (showEventNotes) {
        totalNoteCount += eventNoteCount;
    }

    const { mutateAsync: deletePurchaseNoteItem } = useMutation(
        async (noteId: ID) => {
            try {
                await api.delete(`/purchases/${purchase?.id}/notes/${noteId}`);
            } catch {
                flash.show({ text: "Error on deleting purchase notes", color: "danger" });
            }
        },
        {
            onSuccess: (_data: void, variables: ID) => {
                if (variables) {
                    flash.success("Successfully deleted purchase note");
                    const updatedPurchase = assign(purchase, {
                        notes: purchase?.notes.filter((note: Note) => note.id !== variables),
                    });
                    populateResource("purchases", updatedPurchase);
                }
            },
        },
    );

    const onDeletePurchaseNotes = async (noteId: ID) => {
        await deletePurchaseNoteItem(noteId);
    };

    const { mutateAsync: deleteEventNoteItem } = useMutation(
        async (noteId: ID) => {
            try {
                await api.delete(`/events/${event?.id}/notes/${noteId}`);
            } catch {
                flash.show({ text: "Error on deleting event notes", color: "danger" });
            }
        },
        {
            onSuccess: (_data: void, variables: ID) => {
                if (variables) {
                    flash.success("Successfully deleted event note");
                    const updatedEvent = assign(event, {
                        notes: event?.notes.filter((note: Note) => note.id !== variables),
                    });
                    populateResource("events", updatedEvent);
                }
            },
        },
    );

    const onDeleteEventNotes = async (noteId: ID) => {
        await deleteEventNoteItem(noteId);

        invalidate("purchases", purchase.id);
        invalidate("purchaseItems");
        invalidate("timeslots");
    };

    const handleModalOpen = (e: any, option: AddNoteOption) => {
        e.stopPropagation();
        setSelectedAddNoteOption(option);
    };

    return (
        <div className={className} data-testid="notes-list-per-type">
            <Accordion defaultOpen={shouldAutoExpandNotes}>
                <Accordion.Header>
                    <NotesHeading heading="Notes" size="large" />
                    {totalNoteCount > 0 ? <NotesBadge noteCount={totalNoteCount} /> : null}
                </Accordion.Header>

                <Accordion.Body className="space-y-6">
                    {showEventNotes && (
                        <Accordion defaultOpen={shouldAutoExpandNotes && eventNoteCount > 0}>
                            <Accordion.Header>
                                <NotesHeading heading="Event Notes" />
                                {eventNoteCount > 0 ? <NotesBadge noteCount={eventNoteCount} /> : null}
                                <AddNoteButton
                                    permission={Permission.ADD_EVENT_NOTE}
                                    onClick={(e) => handleModalOpen(e, AddNoteOption.EVENT_NOTE)}
                                />
                            </Accordion.Header>

                            <Accordion.Body>
                                {eventNoteCount > 0 && event ? (
                                    <div className="ml-4">
                                        <EventNoteList event={event} onDelete={onDeleteEventNotes} />
                                    </div>
                                ) : (
                                    <p>No Event Notes</p>
                                )}
                            </Accordion.Body>
                        </Accordion>
                    )}

                    {showCurrentPurchaseNotes ? (
                        <Accordion defaultOpen={shouldAutoExpandNotes && purchaseNoteCount > 0}>
                            <Accordion.Header>
                                <NotesHeading heading="Purchase Notes" />
                                {purchaseNoteCount > 0 ? <NotesBadge noteCount={purchaseNoteCount} /> : null}
                                <AddNoteButton
                                    permission={Permission.ADD_EVENT_NOTE}
                                    onClick={(e) => handleModalOpen(e, AddNoteOption.PURCHASE_NOTE)}
                                />
                            </Accordion.Header>

                            <Accordion.Body>
                                {purchaseNoteCount > 0 ? (
                                    <div className="ml-4">
                                        <PurchaseNoteList purchase={purchase} onDelete={onDeletePurchaseNotes} />
                                    </div>
                                ) : (
                                    <p>No Purchase Notes</p>
                                )}
                            </Accordion.Body>
                        </Accordion>
                    ) : (
                        <BookingNoteList
                            event={event}
                            onAddNoteClick={(e) => handleModalOpen(e, AddNoteOption.PURCHASE_NOTE)}
                        />
                    )}

                    {userId && !isOtherSellerPurchase(purchase, seller.id) && (
                        <Accordion defaultOpen={shouldAutoExpandNotes && customerNoteCount > 0}>
                            <Accordion.Header>
                                <NotesHeading heading="Customer Notes" />
                                {customerNoteCount > 0 ? <NotesBadge noteCount={customerNoteCount} /> : null}
                                <AddNoteButton
                                    permission={Permission.MODIFY_CUSTOMER_DETAILS}
                                    onClick={(e) => handleModalOpen(e, AddNoteOption.CUSTOMER_NOTE)}
                                />
                            </Accordion.Header>

                            <Accordion.Body>
                                {customerNoteCount > 0 ? (
                                    <div className="ml-4">
                                        <CustomerNoteList userId={userId} />
                                    </div>
                                ) : (
                                    <p>No Customer Notes</p>
                                )}
                            </Accordion.Body>
                        </Accordion>
                    )}
                </Accordion.Body>
            </Accordion>

            <CreateCustomerNoteModal
                isOpen={selectedAddNoteOption === AddNoteOption.CUSTOMER_NOTE}
                customerId={userId ?? ""}
                purchaseId={purchase?.id}
                eventId={event?.id}
                onClose={handleModalClose}
            />
            <CreatePurchaseNoteModal
                isOpen={selectedAddNoteOption === AddNoteOption.PURCHASE_NOTE}
                purchase={purchase}
                onClose={handleModalClose}
            />

            <CreateEventNoteModal
                isOpen={selectedAddNoteOption === AddNoteOption.EVENT_NOTE}
                events={event ? [event] : []}
                onClose={handleModalClose}
            />
        </div>
    );
};
